import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Breadcrumb, Button, Col, Form, Image, Input, Radio, Row, Select, Space } from 'antd';
import eiqSecureStore from '../../stores/eiq-secure.store';
import { ReactComponent as AdmitIcon } from 'assets/icons/admit.svg';
import { ReactComponent as TurnbackIcon } from 'assets/icons/turnback.svg';
import { ReactComponent as PhoneIcon } from 'assets/icons/phone.svg';
import { ReactComponent as BackIcon } from 'assets/icons/back.svg';
import { FormWrapper } from '../../../../common/components/form-wrapper/form-wrapper';
import './add-visitor.scss';
import guestService from '../../../../common/services/guest-service/guest.service';
import { notification } from '../../../../common/utils/notification';
import { formatAddressLabel } from '../../../../common/helpers/address.helper';
import { useModal } from '../../../../common/hooks/use-modal/use-modal.hook';
import { RadioChangeEvent } from 'antd/lib';
import TurnbackModal, { ITurnbackModalProps } from '../turnback-modal/turnback-modal';
import ContactsModal from '../contacts-modal/contacts-modal';
import configurationStore from 'common/stores/configuration/configuration.store';
import { GuestType } from 'common/enums/guest-type.enum';
import { addVisitorValidation } from './add-visitor.validation';
import { toGateOptions } from 'features/eiq-secure/constans/common.eiq-secure';
import SelectState from 'common/components/form-elements/select-state/select-state';
import { isServiceGuest } from 'common/helpers/guest-type.helpers';
import { useTableContext } from 'common/components/table/table-context';
import { TurnBack, Admit } from 'features/eiq-secure/constans/admit-actions.constants';
import { useMedia } from '../../../../common/hooks/use-media/use-media.hook';
import { AdmitGuestEnum } from '../../constans/admit-types';
import Footer from '../../../../common/components/footer/footer';
import { setResidentListSearchFilter, setVacantAddressSearchFilter } from 'common/helpers/search-filter.helper';
import EiqSecureGuestPrintInstruction from '../eiq-secure-guest-license/eiq-secure-guest-print-instruction';
import { ReactComponent as PrintIcon } from 'assets/icons/print.svg';
import { useJsApiLoader } from '@react-google-maps/api';
import plateRecognitionContext from 'features/eiq-secure/stores/license-plate-recognition-context';
import { useDefaultAdmitValues } from 'features/eiq-secure/hooks/use-default-admit-values/use-default-admit-values.hook';
import { useBoundPlateRecognition } from 'features/eiq-secure/hooks/use-bound-plate-recognition/use-bound-plate-recognition.hook';
import userAuthStore from 'common/stores/user-auth/user-auth.store';
import featureStore from 'common/stores/features/feature.store';
import { trackPromise } from 'react-promise-tracker';
import citationService from 'common/services/citation-service/citation.service';
import DriveId from '../drive-id/drive-id';
import { useGuestPrintInstruction } from 'features/eiq-secure/hooks/use-guest-license-print';
import { useIdDocument } from 'features/eiq-secure/hooks/use-id-document/use-id-document.hook';
import { prepareVisitorIdDocument } from 'features/eiq-secure/heplers/id-document.helper';
import visitorBanListService from 'common/services/visitor-ban-list-service/visitor-ban-list.service';
import AdvisoryAlertModal, { IAdvisoryAlertModalProps } from '../advisory-alert-modal/advisory-alert-modal';
import { getNotesFromCheckResult } from 'features/eiq-secure/heplers/admit-notes.helper';

interface IAddVisitorProps {
  isVacantAddress: boolean;
}

const createRouteOrigin = (
  latitude: number | null | undefined,
  longitude: number | null | undefined,
): google.maps.LatLngLiteral | undefined => {
  if (typeof latitude !== 'number' || typeof longitude !== 'number' || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return undefined;
  }

  return {
    lat: latitude,
    lng: longitude,
  };
};

const getUnlistedGuestDefaultType = () => {
  const type = configurationStore.configuration?.default.guestType;

  if (type === GuestType.Family) {
    return GuestType.Guest;
  }

  return type;
};

const openModal = (modal: ReturnType<typeof useModal>, initData: IAdvisoryAlertModalProps | ITurnbackModalProps) => {
  modal.setModalInitData(initData);
  modal.showModal();
};

export const AddVisitor = observer(({ isVacantAddress }: IAddVisitorProps) => {
  const tableContext = useTableContext();
  const { isMobileOrTable } = useMedia();

  const selectedAction = useRef<string>();
  const turnbackReason = useRef<string>();
  const isDrivingPrivilegesRevoked = useRef(false);
  const isAdvisoryAdmit = useRef(false);
  const admitNotes = useRef<string | null>(null);

  const [visitorType, setVisitorType] = useState<GuestType | undefined | null>(getUnlistedGuestDefaultType());
  const [isButtonsDisabled, setIsButtonsDisabled] = useState(true);
  const [form] = Form.useForm();

  const { idDocumentRef, onIdDocumentRecognized, onUpdateFromCameraClicked } = useIdDocument({
    form,
    onNamesChanged: () => handleOnValuesChange(null, form.getFieldsValue()),
  });
  const [formValues, setFormValues] = useState<any>({});

  const { defaultGate, defaultLicenseState, defaultLicensePlate, image, camera } = useDefaultAdmitValues({
    currentGuest: null,
  });

  useBoundPlateRecognition(() => {
    const fallbackState = configurationStore.configuration?.default.defaultLicenseState;
    form.setFieldValue('carLicensePlate', '');
    form.setFieldValue('carState', fallbackState);
    form.setFieldValue('gate', userAuthStore.defaultGate || undefined);
    const values = form.getFieldsValue();
    handleOnValuesChange(null, values);
  });

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey });

  const resetAdmission = () => {
    selectedAction.current = '';
    turnbackReason.current = '';
    isDrivingPrivilegesRevoked.current = false;
    isAdvisoryAdmit.current = false;
    admitNotes.current = null;
  };

  const resetState = () => {
    resetAdmission();
    idDocumentRef.current = null;
    form.resetFields();
  };

  useEffect(() => resetState, []);

  const gateOptions = toGateOptions(configurationStore?.configuration?.gates || []);

  const onFinish = async (values: any) => {
    const residentId = eiqSecureStore?.residentDetails?.id;
    const selectedAddressId = eiqSecureStore?.selectedAddressId;
    const selectedVacantAddressId = eiqSecureStore?.selectedVacantAddress?.id;
    const plateRecognitionId = plateRecognitionContext.getRecognitionId(values.gate, values.carLicensePlate, values.carState);

    const idDocument = await prepareVisitorIdDocument(idDocumentRef.current?.getIdDocument(), values);

    const visitorData = {
      ...idDocument,
      entryType: 1,
      image,
      camera,
      ...values,
      plateRecognitionId,
      isDrivingPrivilagesRevoked: isDrivingPrivilegesRevoked.current,
      isAdvisoryAdmit: isAdvisoryAdmit.current,
      note: admitNotes.current,
    };

    const successMessage = selectedAction.current === TurnBack ? 'The visitor has been turned back' : 'The visitor has been admitted';

    if (isVacantAddress && selectedVacantAddressId) {
      await guestService.admitVisitorToVacantAddress(selectedVacantAddressId, visitorData);
      notification.success({ message: successMessage });
      setVacantAddressSearchFilter('');
    }
    if (!isVacantAddress && residentId && selectedAddressId) {
      if (selectedAction.current === Admit) {
        await guestService.admitVisitor(residentId, selectedAddressId, visitorData);
      } else {
        visitorData.reason = turnbackReason.current;
        await guestService.turnbackVisitor(residentId, selectedAddressId, visitorData);
      }
      notification.success({ message: successMessage });
      setResidentListSearchFilter('');
    }
    tableContext.setSelectedRow(null);

    if (isVacantAddress) {
      eiqSecureStore.setAdmitFlow(AdmitGuestEnum.ADD);
    } else {
      eiqSecureStore.setAdmitFlow(null);
      eiqSecureStore.setIsAdmitFlowProcessed();
    }

    plateRecognitionContext.clear();
    resetState();
  };

  const admitGuest = () => {
    selectedAction.current = Admit;
    form.submit();
  };

  const continueAdmission = async () => {
    if (featureStore.isEIQAdvisoryEnabled) {
      const result = await trackPromise(
        visitorBanListService.check({
          licensePlate: form.getFieldValue('carLicensePlate'),
          firstName: form.getFieldValue('firstName'),
          lastName: form.getFieldValue('lastName'),
          companyName: form.getFieldValue('companyName'),
        }),
      );

      isAdvisoryAdmit.current = result.hasMatch || result.hasPartialMatch;
      admitNotes.current = getNotesFromCheckResult(result);

      if (isAdvisoryAdmit.current) {
        const modalInitData: IAdvisoryAlertModalProps = {
          data: result,
          type: 'BanList',
          isVacantAddress: isVacantAddress,
          isShowReason: !!configurationStore.configuration?.advisory.isDisplayReasonForSecureOfficer,
        };

        openModal(visitorBanAlertModal, modalInitData);
        return;
      }
    }

    admitGuest();
  };

  const handleClickAdmit = async () => {
    resetAdmission();

    if (featureStore.isEIQIntelliTicketEnabled) {
      const licensePlate = form.getFieldValue('carLicensePlate');
      const result = await trackPromise(citationService.isLicensePlateDrivingPrivilegesRevoked(licensePlate));
      isDrivingPrivilegesRevoked.current = result.isDrivingPrivilegesRevoked;

      if (isDrivingPrivilegesRevoked.current) {
        const modalInitData: IAdvisoryAlertModalProps = {
          data: result,
          type: 'DrivingPrivileges',
          isVacantAddress: isVacantAddress,
          isShowReason: true,
        };

        openModal(drivingPrivilagesRevokeModal, modalInitData);
        return;
      }
    }

    await continueAdmission();
  };

  const handleClickCancel = () => {
    resetState();
    eiqSecureStore.setAdmitFlow(isMobileOrTable ? AdmitGuestEnum.SELECT_GUEST : null);
  };

  const handleVisitorType = (e: RadioChangeEvent) => {
    setVisitorType(e.target.value);
  };

  const contactsModal = useModal();

  const handleWarningClose = (
    isSuccess: boolean | undefined,
    type: string | undefined,
    initReason: string,
    onAdmit: () => void | Promise<void>,
  ) => {
    if (!isSuccess) {
      resetAdmission();
      return;
    }
    if (type === Admit) {
      return onAdmit();
    }
    if (type === TurnBack) {
      openModal(turnbackModal, { initReason });
    }
  };

  const drivingPrivilagesRevokeModal = useModal({
    onClose: (isSuccess, type) => handleWarningClose(isSuccess, type, 'Driving privileges revoked', continueAdmission),
  });

  const visitorBanAlertModal = useModal({
    onClose: (isSuccess, type) => handleWarningClose(isSuccess, type, 'On banned list', admitGuest),
  });

  const turnbackModal = useModal({
    onClose: (isSuccessSaved, reason) => {
      if (!isSuccessSaved) {
        resetAdmission();
        return;
      }
      turnbackReason.current = reason;
      selectedAction.current = TurnBack;
      form.submit();
    },
  });

  const handleOnValuesChange = (_: any, values: any) => {
    if (
      !values.gate ||
      !values.carLicensePlate ||
      !values.carState ||
      !values.type ||
      (isServiceGuest(values.type) && !values.companyName) ||
      (!isServiceGuest(values.type) && (!values.firstName || !values.lastName))
    ) {
      setIsButtonsDisabled(true);
      return;
    }
    setIsButtonsDisabled(false);
    setFormValues(values);
  };

  useEffect(() => {
    if (!form) {
      setIsButtonsDisabled(true);
      return;
    }
    const values = form.getFieldsValue();
    handleOnValuesChange(null, values);

    const fieldName = isServiceGuest(values.type) ? 'companyName' : 'firstName';
    form.getFieldInstance(fieldName)?.focus();
  }, [form, visitorType]);

  const selectedGate = configurationStore.configuration?.gates.find((gate) => gate.name === formValues?.gate);
  const routeOrigin = createRouteOrigin(selectedGate?.latitude, selectedGate?.longitude);
  const printAddress = isVacantAddress ? eiqSecureStore.selectedVacantAddress : tableContext.getSelectedRow()?.address;
  const { city, state } = configurationStore.communitySettings ?? {};
  const formattedPrintAddress = formatAddressLabel(printAddress);
  const routeDestination = city && state && formattedPrintAddress ? `${state}, ${city}, ${formattedPrintAddress}` : undefined;

  const { printRef, route, handlePrint } = useGuestPrintInstruction({
    isMapsLoaded: isLoaded,
    origin: routeOrigin,
    destination: routeDestination,
    fastAccessEnabled: false,
    obtainFastAccessPass: () => Promise.resolve(''),
  });

  const admitActionsBtn = (
    <>
      <Button type="default" icon={<PrintIcon />} size={'middle'} onClick={handlePrint} className="mobile-btn" disabled={isButtonsDisabled}>
        Print directions
      </Button>
      {!isVacantAddress && (
        <>
          <Button onClick={handleClickCancel}>Cancel</Button>
          <Button
            className="important-btn mobile-btn"
            icon={<TurnbackIcon />}
            onClick={turnbackModal.showModal}
            disabled={isButtonsDisabled}>
            Turnback
          </Button>
          <Button className="secondary-btn mobile-btn" icon={<PhoneIcon />} onClick={contactsModal.showModal}>
            Dial
          </Button>
        </>
      )}
      <Button
        type="primary"
        className="primary-btn mobile-btn"
        icon={<AdmitIcon />}
        onClick={handleClickAdmit}
        disabled={isButtonsDisabled}>
        Approve
      </Button>
    </>
  );

  const handleClickBack = () => {
    eiqSecureStore.setAdmitFlow(isMobileOrTable && !isVacantAddress ? AdmitGuestEnum.SELECT_GUEST : null);
  };

  const getGuestName = () => {
    if (isServiceGuest(formValues?.type)) {
      return formValues?.companyName;
    }
    return `${formValues?.firstName} ${formValues?.lastName}`;
  };
  const isInfoSectionEnabled = () => featureStore.isDriveIdEnabled || featureStore.isPlateScanEnabled;

  return (
    <>
      <EiqSecureGuestPrintInstruction
        ref={printRef}
        address={printAddress}
        gate={formValues?.gate}
        guestLicense={formValues.carLicensePlate}
        guestName={getGuestName()}
        rules={configurationStore.configuration?.default.communityRules}
        communityName={configurationStore.configuration?.default.communityName ?? ''}
        routeOriginAddressString={route?.addressFrom}
        route={route}
        isMinimize={(route?.steps.length ?? 0) > 7}
        speedLimit={configurationStore.configuration?.default.speedLimit}
        speedLimitImageUrl={configurationStore.configuration?.default?.speedLimitImageUrl}
      />
      <div className="gust-add-visitor column-20">
        <Row justify="space-between">
          <Col>
            <div className="space-12">
              <div className="breadcrumb-wrapper">
                {!isVacantAddress ||
                  (isVacantAddress && isMobileOrTable && (
                    <Button icon={<BackIcon />} onClick={handleClickBack}>
                      Back
                    </Button>
                  ))}
                <Breadcrumb
                  separator="–"
                  items={[
                    {
                      title: ' Guests',
                    },
                  ]}
                />
              </div>
              <span className="guest-address">
                {formatAddressLabel(isVacantAddress ? eiqSecureStore.selectedVacantAddress : eiqSecureStore.getSelectedAddress())}
              </span>
            </div>
          </Col>
          <Col>
            <Space size={8} className="buttons-font-16">
              {!isMobileOrTable && admitActionsBtn}
            </Space>
          </Col>
        </Row>
        <div className="container-row">
          <Row gutter={32}>
            {isInfoSectionEnabled() && (
              <Col lg={8} sm={24} className="instructions-wrapper">
                {featureStore.isDriveIdEnabled && (
                  <DriveId
                    onUpdateFromCamera={onUpdateFromCameraClicked}
                    onImageRecognized={onIdDocumentRecognized}
                    ref={idDocumentRef}
                    idDocument={null}
                  />
                )}
                {featureStore.isPlateScanEnabled && image && (
                  <div className="instructions-item">
                    <span className="title">PlateScan ({camera})</span>
                    <Image src={image} alt="License Plate" loading="lazy" />
                  </div>
                )}
              </Col>
            )}
            <Col lg={isInfoSectionEnabled() ? 16 : 24} sm={24}>
              <FormWrapper form={form} onFinish={onFinish} layout="vertical" onValuesChange={handleOnValuesChange}>
                <Row gutter={{ lg: 32, md: 32, xs: 0 }}>
                  <Col lg={isInfoSectionEnabled() ? 8 : 4} sm={24}>
                    <Form.Item name="type" label="Type" rules={addVisitorValidation.type} initialValue={getUnlistedGuestDefaultType()}>
                      <Radio.Group onChange={handleVisitorType}>
                        <Space direction="vertical" size={14}>
                          <Radio value={GuestType.Guest}>Person</Radio>
                          <Radio value={GuestType.Service}>Service</Radio>
                          <Radio value={GuestType.Delivery}>Delivery</Radio>
                          <Radio value={GuestType.RideShare}>Ride share</Radio>
                          <Radio value={GuestType.UrgentService}>Urgent service</Radio>
                          <Radio value={GuestType.Medical}>Health & Medical</Radio>
                          <Radio value={GuestType.Employee}>Employee</Radio>
                        </Space>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col lg={16} sm={24}>
                    <Row gutter={21}>
                      <Col span={isInfoSectionEnabled() ? 24 : 12}>
                        {isServiceGuest(visitorType) && (
                          <Form.Item name="companyName" label="Company name" rules={addVisitorValidation.companyName}>
                            <Input placeholder="Enter company name" />
                          </Form.Item>
                        )}
                        <Form.Item
                          name="firstName"
                          label="First name"
                          rules={!isServiceGuest(visitorType) ? addVisitorValidation.firstName : undefined}>
                          <Input placeholder="Enter first name" />
                        </Form.Item>
                        <Form.Item
                          name="lastName"
                          label="Last name"
                          rules={!isServiceGuest(visitorType) ? addVisitorValidation.lastName : undefined}>
                          <Input placeholder="Enter last name" />
                        </Form.Item>
                      </Col>
                      <Col span={isInfoSectionEnabled() ? 24 : 12}>
                        <Form.Item
                          name="carState"
                          label="License State"
                          initialValue={defaultLicenseState}
                          rules={addVisitorValidation.carState}>
                          <SelectState placeholder="Select a license state" popupClassName="so-select" />
                        </Form.Item>
                        <Form.Item
                          name="carLicensePlate"
                          initialValue={defaultLicensePlate}
                          label="License Plate"
                          rules={addVisitorValidation.carLicensePlate}>
                          <Input placeholder="Enter license plate" onPressEnter={handleClickAdmit} />
                        </Form.Item>
                        <Form.Item name="gate" label="Location (Gate)" rules={addVisitorValidation.gate} initialValue={defaultGate}>
                          <Select options={gateOptions} placeholder="Select Location (Gate)" popupClassName="so-select" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </FormWrapper>
            </Col>
          </Row>
        </div>
      </div>
      {isMobileOrTable && (
        <Footer>
          <Space size={8}>{admitActionsBtn}</Space>
        </Footer>
      )}
      <ContactsModal title="Contacts" isOpen={contactsModal?.isOpenModal} onClose={contactsModal?.hideModal} />
      <TurnbackModal
        title="Turnback"
        isOpen={turnbackModal?.isOpenModal}
        onClose={turnbackModal?.hideModal}
        initData={turnbackModal.initData}
      />
      <AdvisoryAlertModal
        isOpen={drivingPrivilagesRevokeModal.isOpenModal}
        onClose={drivingPrivilagesRevokeModal.hideModal}
        initData={drivingPrivilagesRevokeModal.initData}
      />
      <AdvisoryAlertModal
        isOpen={visitorBanAlertModal.isOpenModal}
        onClose={visitorBanAlertModal.hideModal}
        initData={visitorBanAlertModal.initData}
      />
    </>
  );
});
